import React, { useState, useEffect } from "react";
import axios from "axios";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./ViewStudent.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const API_URL = "https://aadyaacademy.org/api";

// Calendar Component (unchanged)
const CustomCalendar = ({ attendance, formatDate, monthYear, setMonthYear }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    setCurrentDate(new Date(monthYear.year, monthYear.month));
  }, [monthYear]);

  const today = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const daysOfWeek = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
  const calendarDays = [
    ...Array(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const getDateClassName = (day) => {
    if (!day) return "";
    const isToday =
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear();
    const dateKey = formatDate(new Date(year, month, day));
    if (attendance[dateKey] === "present") return "present-date";
    if (attendance[dateKey] === "absent") return "absent-date";
    if (attendance[dateKey] === "half day") return "halfday-date";
    return isToday ? "today-date" : "";
  };

  const handleMonthChange = (e) => {
    const [y, m] = e.target.value.split("-");
    setMonthYear({ year: +y, month: +m });
  };

  const handlePrevMonth = () => {
    const newDate = new Date(year, month - 1);
    setMonthYear({ year: newDate.getFullYear(), month: newDate.getMonth() });
  };

  const handleNextMonth = () => {
    const newDate = new Date(year, month + 1);
    setMonthYear({ year: newDate.getFullYear(), month: newDate.getMonth() });
  };

  const monthOptions = Array.from({ length: 24 }, (_, i) => {
    const date = new Date(2024, i);
    return {
      label: `${date.toLocaleString("default", {
        month: "long",
      })} ${date.getFullYear()}`,
      value: `${date.getFullYear()}-${date.getMonth()}`,
    };
  });

  return (
    <div className="custom-calendar">
      <div className="calendar-header">
        <button onClick={handlePrevMonth}>&lt;</button>
        <select value={`${year}-${month}`} onChange={handleMonthChange}>
          {monthOptions.map((option, i) => (
            <option key={i} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button onClick={handleNextMonth}>&gt;</button>
      </div>
      <div className="calendar-weekdays">
        {daysOfWeek.map((d, i) => (
          <div key={i} className="weekday">
            {d}
          </div>
        ))}
      </div>
      <div className="calendar-days">
        {calendarDays.map((day, i) => (
          <div key={i} className={`calendar-day ${getDateClassName(day)}`}>
            {day || ""}
          </div>
        ))}
      </div>
    </div>
  );
};

function ViewStudent({ student, onClose }) {
  const [studentData, setStudentData] = useState(null);
  const [attendance, setAttendance] = useState({});
  const [attendanceCounts, setAttendanceCounts] = useState({
    present: 0,
    absent: 0,
    halfDay: 0,
  });
  const [monthYear, setMonthYear] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
  });
  const [reports, setReports] = useState([]); // New state for reports
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatDate = (date) => date.toLocaleDateString("en-CA");

  const calculateCounts = (data) => {
    const counts = { present: 0, absent: 0, halfDay: 0 };
    Object.entries(data).forEach(([dateStr, status]) => {
      const d = new Date(dateStr);
      if (d.getFullYear() === monthYear.year && d.getMonth() === monthYear.month) {
        if (status === "present") counts.present++;
        if (status === "absent") counts.absent++;
        if (status === "half day") counts.halfDay++;
      }
    });
    setAttendanceCounts(counts);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (student) {
          setStudentData(student);
          // Fetch attendance
          const attendanceResponse = await axios.get(
            `${API_URL}/parent/api/attendance?father_phone=${student.father_phone}&student_id=${student.student_id}`
          );
          setAttendance(attendanceResponse.data);

          // Fetch reports
          const reportsResponse = await axios.get(
            `${API_URL}/parents/reports?student_id=${student.student_id}`
          );
          setReports(reportsResponse.data);
        }
      } catch (err) {
        setError("Failed to fetch student data, attendance, or reports.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [student]);

  useEffect(() => {
    calculateCounts(attendance);
  }, [attendance, monthYear]);

  if (loading)
    return (
      <div className="view-student-container">
        <h2>Loading...</h2>
      </div>
    );
  if (error)
    return (
      <div className="view-student-container">
        <h2>{error}</h2>
      </div>
    );

  const chartData = {
    labels: ["Present", "Absent", "Half Day"],
    datasets: [
      {
        label: "Attendance",
        data: [
          attendanceCounts.present,
          attendanceCounts.absent,
          attendanceCounts.halfDay,
        ],
        backgroundColor: ["#4CAF50", "#f44336", "#FFEB3B"],
      },
    ],
  };

  return (
    <div className="view-student-container">
      <div className="view-stu-c">
        <div className="view-stu-header">
          <h2>Student Details</h2>
        </div>
        <div className="stu-id-name-class-sec">
          <div className="stu-id">
            <h4>Student ID</h4>
            <p>{studentData.student_id}</p>
          </div>
          <div className="stu-name">
            <h4>Name</h4>
            <p>{studentData.student_name}</p>
          </div>
          <div className="stu-class">
            <h4>Class</h4>
            <p>{studentData.class}</p>
          </div>
          <div className="stu-section">
            <h4>Section</h4>
            <p>{studentData.section}</p>
          </div>
        </div>
        <div className="stu-gender-dob-academic">
          <div className="stu-gender">
            <h4>Gender</h4>
            <p>{studentData.gender}</p>
          </div>
          <div className="stu-dob">
            <h4>DOB</h4>
            <p>{formatDate(new Date(studentData.dob))}</p>
          </div>
          <div className="stu-academic">
            <h4>Academic Year Joined</h4>
            <p>{studentData.academic_year_joined}</p>
          </div>
        </div>
        <div className="stu-address-row">
          <div className="stu-address">
            <h4>Address</h4>
            <p>{studentData.address}</p>
          </div>
        </div>
        <div className="stu-parents">
          <div className="stu-father-name">
            <h4>Father Name</h4>
            <p>{studentData.father_name}</p>
          </div>
          <div className="stu-father-phone">
            <h4>Father Phone</h4>
            <p>{studentData.father_phone || "N/A"}</p>
          </div>
          <div className="stu-mother-name">
            <h4>Mother Name</h4>
            <p>{studentData.mother_name}</p>
          </div>
          <div className="stu-mother-phone">
            <h4>Mother Phone</h4>
            <p>{studentData.mother_phone || "N/A"}</p>
          </div>
        </div>

        {/* Attendance Section */}
        <div className="student-attendance-section">
          <div className="calendar-chart-wrapper">
            <CustomCalendar
              attendance={attendance}
              formatDate={formatDate}
              monthYear={monthYear}
              setMonthYear={setMonthYear}
            />
            <div className="chart-container">
              <Bar
                data={chartData}
                options={{ responsive: true, plugins: { legend: { position: "top" } } }}
              />
            </div>
          </div>
        </div>

        {/* Reports Section */}
        <div className="student-reports-section">
          <h2>Student Reports</h2>
          {reports.length > 0 ? (
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Test Name</th>
                  <th>File</th>
                  <th>Uploaded At</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report, index) => (
                  <tr key={index}>
                    <td>{report.test_name}</td>
                    <td>
                      {report.file_path ? (
                        <a
                          href={`${API_URL}/${report.file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="report-link"
                        >
                          View Report
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td>
                      {new Date(report.test_date).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No reports available for this student.</p>
          )}
        </div>

        <div className="view-stu-btn">
          <button className="view-stu-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ViewStudent;
